/* deploy-marker: iter17-smooth-progress */
/*
 * Yutrients image funnel — controller for /pages/match-your-blend.
 *
 * Iter-3 (2026-06-03): UPLOAD UX redesign. Single drop card with a
 * 4-state machine (idle → uploading → almost → uploaded) and a
 * persistent action button stack below.
 *
 * Multi-file flow: customer can drop several files at once OR click
 * "UPLOAD ANOTHER IMAGE" to add more. Files queue and upload one at a
 * time. Each upload cycles the card through uploading → almost →
 * uploaded. Successfully uploaded files accumulate as a thumb-stack in
 * the uploaded card. ANALYZE MY LABEL sends every queued image_id to
 * /image/analyse and redirects to BYO with the resulting formula.
 *
 * Fake-progress: fetch() doesn't give us progress events, so we simulate
 * a smooth fill to ~60% during the upload (UPLOADING state), then snap
 * to ~95% with the "Almost there…" label (ALMOST state), then to 100%
 * on resolve before flipping to UPLOADED.
 */

(function () {
  'use strict';

  const SECTION_SELECTOR = '[data-yt-image-funnel]';
  const BYO_PATH = '/pages/build-your-own';

  // Fake-progress timings — tuned to feel honest at typical 2-3MB photos.
  // Iter-17: one continuous animation from 0% → 95% over the whole
  // visible upload window (uploading + almost). No mid-flight pause
  // or jump. The bar reaches ~80% by the time we flip from
  // "Uploading…" to "Almost there…" and keeps climbing to 95% in
  // the almost state. Final hop to 100% happens silently on flip
  // to uploaded.
  const PROGRESS_TOTAL_DURATION_MS = 5200; // uploading hold + almost hold
  const PROGRESS_FILL_TO_PCT = 95;         // ceiling during almost
  const PROGRESS_HOLD_AT_PCT = 80;         // where we are when uploading ends
  const PROGRESS_UPLOADING_MS = 4500;      // time before flipping to almost
  const ALMOST_MIN_VISIBLE_MS = 700;       // never flash for less

  class ImageFunnelController {
    constructor(root) {
      this.root = root;
      this.appProxyBase = root.getAttribute('data-yt-app-proxy-base') || '/apps/yutrients';

      // DOM cache
      this.shell      = root.querySelector('[data-yt-shell]');
      this.dropZone   = root.querySelector('[data-yt-drop]');
      this.dropInput  = root.querySelector('[data-yt-drop-input]');
      this.actions    = root.querySelector('[data-yt-actions]');
      this.preCta     = root.querySelector('[data-yt-pre-cta]');
      this.postPair   = root.querySelector('[data-yt-post-actions]');
      this.uploadAnother = root.querySelector('[data-yt-upload-another]');
      this.analyseBtn = root.querySelector('[data-yt-analyse]');
      this.loading    = root.querySelector('[data-yt-loading]');
      this.errorRegion = root.querySelector('[data-yt-error]');
      // Per-state nodes inside the drop card
      this.states = {
        idle:      root.querySelector('[data-yt-state="idle"]'),
        uploading: root.querySelector('[data-yt-state="uploading"]'),
        almost:    root.querySelector('[data-yt-state="almost"]'),
        uploaded:  root.querySelector('[data-yt-state="uploaded"]'),
      };
      this.uploadingLabel = root.querySelector('[data-yt-uploading-label]');
      this.progressBar = root.querySelector('[data-yt-progress-bar]');
      this.thumbStack = root.querySelector('[data-yt-thumb-stack]');
      this.uploadedName = root.querySelector('[data-yt-uploaded-name]');
      this.uploadedSize = root.querySelector('[data-yt-uploaded-size]');
      // iter-14: photo-quality modal
      this.retakeModal = root.querySelector('[data-yt-retake]');
      this.retakeRestartBtn = root.querySelector('[data-yt-retake-restart]');
      this.retakeBackdrop = root.querySelector('[data-yt-retake-dismiss]');

      // Runtime state
      this.sessionId = null;
      this.queue = [];                 // pending files
      this.uploaded = [];              // { id, file, imageId, previewUrl }
      this.localIdCounter = 0;
      this.isUploading = false;        // single-flight guard

      this.bind();
      this.setState('idle');
    }

    bind() {
      // File picker — click whole card OR Enter/Space
      this.dropInput.addEventListener('change', (e) => {
        const files = Array.from(e.target.files || []);
        this.enqueue(files);
        e.target.value = ''; // allow re-picking the same file
      });
      this.dropZone.addEventListener('click', () => this.openPicker());
      this.dropZone.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.openPicker();
        }
      });

      // Drag-and-drop — bind on the outer shell so the customer can
      // drop anywhere on the dashed card (including under the disabled
      // CTA, since iter-4 wraps both in the same shell).
      const dndTarget = this.shell || this.dropZone;
      ['dragenter', 'dragover'].forEach((evt) => {
        dndTarget.addEventListener(evt, (e) => {
          e.preventDefault();
          dndTarget.classList.add('is-dragover');
        });
      });
      ['dragleave', 'dragend'].forEach((evt) => {
        dndTarget.addEventListener(evt, () => {
          dndTarget.classList.remove('is-dragover');
        });
      });
      dndTarget.addEventListener('drop', (e) => {
        e.preventDefault();
        dndTarget.classList.remove('is-dragover');
        const dropped = Array.from(e.dataTransfer?.files || []);
        const files = dropped.filter((f) => f.type.startsWith('image/'));
        // Dropped something but nothing was an image — tell the customer
        // rather than silently swallowing the drop.
        if (dropped.length && !files.length) {
          this.showError('That doesn’t look like an image — please drop a photo of your label.');
          return;
        }
        this.enqueue(files);
      });

      // Post-upload actions
      this.uploadAnother.addEventListener('click', (e) => {
        e.stopPropagation();
        this.openPicker();
      });
      this.analyseBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.analyse();
      });
      // iter-14: retake modal bindings
      if (this.retakeRestartBtn) {
        this.retakeRestartBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          this.closeRetake();
          this.restartFlow();
        });
      }
      if (this.retakeBackdrop) {
        this.retakeBackdrop.addEventListener('click', () => this.closeRetake());
      }
      // Esc to dismiss
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.retakeModal && !this.retakeModal.hidden) {
          this.closeRetake();
        }
      });
    }

    // ─── retake modal ──────────────────────────────────────────────

    openRetake() {
      if (!this.retakeModal) return;
      this.retakeModal.hidden = false;
      // Lock body scroll while modal is up.
      document.documentElement.style.overflow = 'hidden';
      // Focus the primary CTA for keyboard users.
      if (this.retakeRestartBtn) {
        setTimeout(() => this.retakeRestartBtn.focus(), 30);
      }
    }
    closeRetake() {
      if (!this.retakeModal) return;
      this.retakeModal.hidden = true;
      document.documentElement.style.overflow = '';
    }

    /**
     * Reset the funnel to the IDLE state — empties the uploaded list,
     * clears thumbs, drops the pending file queue, frees blob URLs.
     * Session is preserved so the customer doesn't re-auth.
     */
    restartFlow() {
      // Revoke any active blob URLs.
      this.uploaded.forEach((u) => {
        if (u.previewUrl) {
          try { URL.revokeObjectURL(u.previewUrl); } catch (e) {}
        }
      });
      this.uploaded = [];
      this.queue = [];
      this.isUploading = false;
      this.thumbStack.innerHTML = '';
      this.uploadedName.textContent = '';
      this.uploadedSize.textContent = '';
      this.hideError();
      this.setState('idle');
      // Auto-open the picker for the user — they came here to retake.
      this.dropInput.click();
    }

    openPicker() {
      // Prevent triggering during in-flight upload — the card is busy.
      if (this.isUploading) return;
      this.dropInput.click();
    }

    // ─── state machine ────────────────────────────────────────────

    setState(state) {
      this.dropZone.setAttribute('data-state', state);
      this.actions.setAttribute('data-state', state);
      Object.entries(this.states).forEach(([key, el]) => {
        if (el) el.hidden = key !== state;
      });
      this.hideError();
    }

    // ─── enqueue + process ────────────────────────────────────────

    enqueue(files) {
      if (!files || !files.length) return;
      // Analytics: file COUNT only — never filenames or image data.
      if (window.ytTrack) ytTrack('match_upload_selected', { file_count: files.length });
      this.hideError();
      files.forEach((f) => this.queue.push(f));
      this.processQueue();
    }

    async processQueue() {
      if (this.isUploading) return;          // already running
      const next = this.queue.shift();
      if (!next) return;                     // nothing to do
      this.isUploading = true;

      try {
        await this.runOneUpload(next);
      } catch (err) {
        this.showError(err.message || 'Upload failed.');
        // Revert to last known good state — uploaded if any, else idle.
        this.setState(this.uploaded.length ? 'uploaded' : 'idle');
      } finally {
        this.isUploading = false;
      }

      // If more files queued, recurse.
      if (this.queue.length) {
        this.processQueue();
      }
    }

    async runOneUpload(file) {
      // 1. Set the filename label. Progress bar animation is driven by
      //    the Web Animations API directly from JS — more reliable than
      //    CSS @keyframes because we can guarantee a fresh animation
      //    instance each time setState('uploading') runs.
      this.uploadingLabel.textContent = `Uploading ${this.shortenName(file.name)}`;

      // Reset bar to 0% with NO transition so the reset is instant.
      this.progressBar.style.transition = 'none';
      this.progressBar.style.width = '0%';
      this.progressBar.classList.remove('yt-image-funnel__progress-bar--almost');

      this.setState('uploading');

      // 2. Trigger ONE continuous fill from 0% → 95% over the whole
      //    upload window (~5.2s) so the bar never appears to pause or
      //    jump. Double rAF guarantees the browser has committed the
      //    initial style (transition:none, width:0%) BEFORE we set the
      //    new width — otherwise the browser collapses both writes into
      //    one paint and no animation runs.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.progressBar.style.transition =
            `width ${PROGRESS_TOTAL_DURATION_MS}ms linear`;
          this.progressBar.style.width = PROGRESS_FILL_TO_PCT + '%';
        });
      });

      // 3. Fire the real upload concurrently with the fake progress.
      //    Build the preview URL upfront so the Image() pre-warm happens
      //    in parallel with the network call — no flash on first show.
      const realUpload = this.uploadFile(file);
      const previewUrl = this.tryCreatePreview(file);
      // Start preloading the preview in parallel; we'll await it later
      // so the thumb only paints when the bytes are fully decoded.
      const previewReady = previewUrl ? this.warmPreview(previewUrl) : Promise.resolve();

      // 4. Hold UPLOADING for the FULL fake-fill duration so the bar
      //    has time to animate. Even if the real upload finishes in
      //    100ms, we keep the visual at uploading. Customer perception
      //    of "fast" beats "instant" — the bar moving is honest about
      //    "we did the work, here's what it looked like".
      const uploadingHold = new Promise((r) => setTimeout(r, PROGRESS_UPLOADING_MS));
      // We still need to know the real upload status. Wait for the
      // longer of the two; capture the result safely.
      let uploadResult, uploadError;
      try {
        const [r] = await Promise.all([realUpload, uploadingHold]);
        uploadResult = r;
      } catch (err) {
        uploadError = err;
        await uploadingHold;
      }

      // If the real upload errored, throw — runOneUpload caller catches.
      if (uploadError) throw uploadError;

      // 5. Flip to ALMOST and hold for ALMOST_MIN_VISIBLE_MS so the
      //    "Almost there…" + 95% bar register visually. While we hold
      //    the user-facing pause, also wait for the preview image to
      //    be fully decoded — that way the thumb paints with the real
      //    image in one shot, no default-tile glitch.
      // Do NOT toggle the --almost class — that would snap the bar to
      // 95% instantly. The continuous transition is still in flight,
      // already heading to 95%. Just switch the state's label.
      this.setState('almost');
      await Promise.all([
        new Promise((r) => setTimeout(r, ALMOST_MIN_VISIBLE_MS)),
        previewReady,
      ]);

      // 6. Append to uploaded list + flip to UPLOADED.
      const localId = ++this.localIdCounter;
      this.uploaded.push({ localId, file, imageId: uploadResult.image_id, previewUrl });
      this.renderUploaded();
      this.setState('uploaded');
    }

    /**
     * Pre-load the preview image into a detached Image so the browser
     * has it cached by the time we paint the thumb. Without this, the
     * thumb shows the default-tile fallback for a frame before the
     * blob URL renders — the "glitch between actual and default"
     * the customer saw.
     */
    warmPreview(url) {
      // Decode the preview image and resolve when it's ready. Caller
      // can `await` to guarantee the thumb paints in one shot — no
      // "default tile flash then real preview" glitch.
      return new Promise((resolve) => {
        const img = new Image();
        img.onload = img.onerror = () => resolve();
        img.src = url;
      });
    }

    /**
     * fetch() bounded by an AbortController so a hung upstream (the
     * App Proxy kills long requests; /image/analyse has 500'd/stalled
     * before) fails predictably instead of spinning forever. A timeout
     * surfaces to the caller as a network error the customer can retry
     * from, rather than a dead spinner + a permanently busy card.
     */
    async fetchWithTimeout(url, opts, timeoutMs) {
      const ctrl = new AbortController();
      const timer = setTimeout(() => ctrl.abort(), timeoutMs);
      try {
        return await fetch(url, { ...opts, signal: ctrl.signal });
      } catch (err) {
        if (err && err.name === 'AbortError') {
          throw new Error('That took too long — please try again.');
        }
        throw err;
      } finally {
        clearTimeout(timer);
      }
    }

    async uploadFile(file) {
      const form = new FormData();
      form.append('files', file);

      const url = this.sessionId
        ? `${this.appProxyBase}/image/upload?session_id=${encodeURIComponent(this.sessionId)}`
        : `${this.appProxyBase}/image/upload`;

      const res = await this.fetchWithTimeout(url, {
        method: 'POST',
        body: form,
        credentials: 'include',
      }, 45000);
      if (!res.ok) {
        const text = await res.text().catch(() => '');
        throw new Error(this.extractErrorMessage(text) || `Upload failed (${res.status})`);
      }
      const data = await res.json();
      if (!this.sessionId && data.session_id) {
        this.sessionId = data.session_id;
      }
      return data;
    }

    // ─── uploaded render ──────────────────────────────────────────

    renderUploaded() {
      // 3-column grid of all uploaded images. Each tile shows the
      // customer's preview (or the default camera tile if preview
      // failed) and has its own ✕ remove button.
      this.thumbStack.innerHTML = '';
      this.uploaded.forEach((u) => {
        const thumb = document.createElement('div');
        thumb.className = 'yt-image-funnel__thumb';
        if (u.previewUrl) {
          // Single bg-image — no layered fallback. The default-tile
          // glitch only happens when both are layered with cover sizing.
          thumb.style.backgroundImage = `url('${u.previewUrl}')`;
          thumb.style.backgroundColor = 'var(--yt-canvas-off-white)';
        } else {
          thumb.style.backgroundImage = `url('${this.defaultThumbUrl()}')`;
          thumb.style.backgroundColor = 'transparent';
        }
        // ✕ remove button on every tile (not just the newest).
        const x = document.createElement('button');
        x.type = 'button';
        x.className = 'yt-image-funnel__thumb-remove';
        x.setAttribute('aria-label', 'Remove ' + u.file.name);
        x.innerHTML = '&times;';
        x.addEventListener('click', (e) => {
          e.stopPropagation();
          this.removeUploaded(u.localId);
        });
        thumb.appendChild(x);
        this.thumbStack.appendChild(thumb);
      });

      // Filename + size — most recent file's metadata in the header.
      const newest = this.uploaded[this.uploaded.length - 1];
      if (newest) {
        this.uploadedName.textContent = this.shortenName(newest.file.name);
        this.uploadedSize.textContent = ' · ' + this.humanSize(newest.file.size);
      }
    }

    defaultThumbUrl() {
      // Cache the asset URL on first lookup. Set via inline data
      // attribute on the section root (populated by Liquid asset_url).
      if (!this._defaultThumbUrl) {
        // Resolve via the Shopify asset folder — same path the Liquid
        // {{ 'yt-upload-thumb-default.png' | asset_url }} would emit.
        const tag = document.querySelector('link[rel="stylesheet"][href*="yt-image-funnel.css"]');
        // Derive the assets base URL from the loaded CSS link.
        if (tag) {
          const base = tag.href.replace(/yt-image-funnel\.css.*$/, '');
          this._defaultThumbUrl = base + 'yt-upload-thumb-default.png';
        } else {
          // Fall back to a relative path (works on Shopify dev store).
          this._defaultThumbUrl = '/cdn/shop/files/yt-upload-thumb-default.png';
        }
      }
      return this._defaultThumbUrl;
    }

    removeUploaded(localId) {
      const idx = this.uploaded.findIndex((u) => u.localId === localId);
      if (idx === -1) return;
      const [removed] = this.uploaded.splice(idx, 1);
      if (removed.previewUrl) {
        try { URL.revokeObjectURL(removed.previewUrl); } catch (_) {}
      }
      if (this.uploaded.length === 0) {
        this.setState('idle');
      } else {
        this.renderUploaded();
      }
    }

    // ─── analyse ──────────────────────────────────────────────────

    async analyse() {
      if (!this.uploaded.length) {
        this.showError('Upload at least one label first.');
        return;
      }
      this.hideError();
      if (window.ytTrack) ytTrack('match_analyse_started', { image_count: this.uploaded.length });
      this.loading.hidden = false;
      this.analyseBtn.disabled = true;
      this.analyseBtn.setAttribute('aria-busy', 'true');

      const body = {
        image_ids: this.uploaded.map((u) => u.imageId),
      };
      if (this.sessionId) body.session_id = this.sessionId;

      // Analytics: short failure code carried from the throw sites below to
      // the catch (which also sees plain network/timeout errors).
      let failReason = null;
      try {
        const res = await this.fetchWithTimeout(`${this.appProxyBase}/image/analyse`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
          credentials: 'include',
        }, 60000);
        if (!res.ok) {
          const text = await res.text().catch(() => '');
          const reqId = res.headers.get('x-request-id') || res.headers.get('x-shopify-request-id') || '';
          // Log everything we have to the console so support tickets
          // include the technical context. The visible message is
          // brand-friendly.
          console.error('[image-funnel] analyse failed', {
            status: res.status, reqId, body: text,
          });
          // Distinguish "your photos are no good" (open retake modal)
          // from "our server fell over" (toast with ref).
          const code = this.extractErrorCode(text);
          if (
            code === 'image_unreadable' ||
            code === 'ai_validation_retry_exhausted' ||
            res.status === 422
          ) {
            // Hide the busy spinner before showing the modal.
            this.loading.hidden = true;
            this.analyseBtn.disabled = false;
            this.analyseBtn.removeAttribute('aria-busy');
            if (window.ytTrack) ytTrack('match_photo_too_poor', { reason: code || `http_${res.status}` });
            this.openRetake();
            return;
          }
          failReason = code || `http_${res.status}`;
          const friendly = this.extractErrorMessage(text);
          const suffix = reqId ? ` (ref ${reqId.slice(0, 12)})` : '';
          throw new Error((friendly || `Analyse failed (${res.status})`) + suffix);
        }
        const data = await res.json();
        if (!data.formula_id) {
          failReason = 'no_formula';
          throw new Error('Analyse returned no formula. Try again.');
        }
        if (window.ytTrack) {
          ytTrack('match_analyse_completed', {
            ingredient_count: Array.isArray(data.ingredients) ? data.ingredients.length : 0,
          });
        }
        window.location.assign(`${BYO_PATH}?stack=${encodeURIComponent(data.formula_id)}`);
      } catch (err) {
        this.loading.hidden = true;
        this.analyseBtn.disabled = false;
        this.analyseBtn.removeAttribute('aria-busy');
        if (window.ytTrack) ytTrack('match_analyse_failed', { reason: failReason || 'network' });
        this.showError(err.message || 'Analyse failed. Please try again.');
      }
    }

    // ─── helpers ──────────────────────────────────────────────────

    tryCreatePreview(file) {
      // HEIC/HEIF browsers can't render natively in <img>; URL.createObject
      // URL still works but the image won't render. For JPEG/PNG/WebP it's
      // fine. We return the URL and let CSS background-image either render
      // it or fall through to the placeholder icon when load fails.
      try {
        if (/^image\/(jpe?g|png|webp|gif)$/i.test(file.type)) {
          return URL.createObjectURL(file);
        }
      } catch (_) {}
      return null;
    }

    shortenName(name) {
      if (!name) return '';
      if (name.length <= 32) return name;
      const dot = name.lastIndexOf('.');
      if (dot < 0 || dot < name.length - 8) return name.slice(0, 28) + '…';
      const ext = name.slice(dot);
      return name.slice(0, 28 - ext.length) + '…' + ext;
    }

    humanSize(bytes) {
      if (!isFinite(bytes) || bytes <= 0) return '';
      if (bytes < 1024) return `${bytes}b`;
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)}kb`;
      return `${(bytes / 1024 / 1024).toFixed(1)}mb`;
    }

    showError(message) {
      if (!this.errorRegion) return;
      this.errorRegion.textContent = message;
      this.errorRegion.hidden = false;
    }
    hideError() {
      if (!this.errorRegion) return;
      this.errorRegion.hidden = true;
      this.errorRegion.textContent = '';
    }

    extractErrorMessage(text) {
      try {
        const json = JSON.parse(text);
        if (json && json.error && json.error.message) return json.error.message;
      } catch (_) {}
      return null;
    }

    extractErrorCode(text) {
      try {
        const json = JSON.parse(text);
        if (json && json.error && json.error.code) return json.error.code;
      } catch (_) {}
      return null;
    }

    escapeHtml(s) {
      return String(s).replace(/[&<>"']/g, (c) => ({
        '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
      }[c]));
    }
  }

  function init() {
    const root = document.querySelector(SECTION_SELECTOR);
    if (!root) return;
    new ImageFunnelController(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // bfcache restore: hitting Back after the analyse redirect can restore a
  // frozen page with the loading spinner still up and the analyse button
  // disabled. Reload so the customer gets a fresh, interactive funnel.
  window.addEventListener('pageshow', (e) => {
    if (e.persisted) window.location.reload();
  });
})();
